import React, { useEffect, useState, useRef } from "react"
import "./EditSchool.scss"
import SchoolApi from "Api/SchoolApi"
import * as Yup from "yup"
import { useNavigate, useParams } from "react-router-dom"
import { Button, Card, CardBody, Col, Row, Spinner } from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import { InputText } from "primereact/inputtext"
import Country from "../../../utils/Data/Country"
import { Dropdown } from "primereact/dropdown"
import { InputTextarea } from "primereact/inputtextarea"
import { Toast } from "primereact/toast"
import { MultiSelect } from "primereact/multiselect"
import { Checkbox } from "primereact/checkbox"

const EditSchool = () => {
    document.title = "Edit School"
    const [loading, setLoading] = useState(false)
    const [pageLoading, setPageLoading] = useState(true)
    const toast = useRef(null)
    const navigate = useNavigate("")
    const { schoolCode } = useParams()
    const accessToken = localStorage.getItem("accessToken")

    const [schoolData, setSchoolData] = useState({})
    const [errors, setErrors] = useState({})

    const typeOptions = [
        { name: "Private", code: "Private" },
        { name: "Government", code: "Government" },
        { name: "International", code: "International" },
    ]

    const affiliationBoardOptions = [
        { name: "State Board", code: "State Board" },
        { name: "CBSE", code: "CBSE" },
        { name: "IB", code: "IB" },
        { name: "CAIE", code: "CAIE" },
        { name: "IGCSE", code: "IGCSE" },
        { name: "ICSE", code: "ICSE" },
    ]

    const yesNoOptions = [
        { name: "Yes", code: "Yes" },
        { name: "No", code: "No" },
    ]

    const gradesOptions = [
        { name: "Pre-KG", code: "Pre-KG" },
        { name: "LKG", code: "LKG" },
        { name: "UKG", code: "UKG" },
        { name: "1st", code: "1st" },
        { name: "2nd", code: "2nd" },
        { name: "3rd", code: "3rd" },
        { name: "4th", code: "4th" },
        { name: "5th", code: "5th" },
        { name: "6th", code: "6th" },
        { name: "7th", code: "7th" },
        { name: "8th", code: "8th" },
        { name: "9th", code: "9th" },
        { name: "10th", code: "10th" },
        { name: "11th", code: "11th" },
        { name: "12th", code: "12th" },
    ]

    useEffect(() => {
        if (schoolCode) {
            getSchoolDetails()
        }
    }, [schoolCode])

    const getSchoolDetails = async () => {
        setPageLoading(true)
        try {
            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }
            const response = await SchoolApi.getSchoolById(schoolCode, config)
            if (response.data.status) {
                const school = response.data.school

                // Map type array to multiselect format
                const typeValues = Array.isArray(school.type)
                    ? school.type.map(t => typeOptions.find(opt => opt.code === t) || { name: t, code: t })
                    : []

                // Map affiliation board to multiselect format
                const affiliationValues = Array.isArray(school.affiliationBoard)
                    ? school.affiliationBoard.map(b => affiliationBoardOptions.find(opt => opt.code === b) || { name: b, code: b })
                    : []

                // Handle grades offered as string or array
                const gradesValue = Array.isArray(school.gradesOffered)
                    ? school.gradesOffered.join(", ")
                    : school.gradesOffered || ""

                // Find country object
                const countryValue = Country.find(c => c.name === school.country)

                // Map boolean facilities to Yes/No options
                const sportsAvailableValue = school.sportsAvailable
                    ? yesNoOptions.find(opt => opt.code === "Yes")
                    : yesNoOptions.find(opt => opt.code === "No")

                const transportFacilitiesValue = school.transportFacilities
                    ? yesNoOptions.find(opt => opt.code === "Yes")
                    : yesNoOptions.find(opt => opt.code === "No")

                const hostelFacilityValue = school.hostelFacility
                    ? yesNoOptions.find(opt => opt.code === "Yes")
                    : yesNoOptions.find(opt => opt.code === "No")

                setSchoolData({
                    ...school,
                    type: typeValues,
                    affiliationBoard: affiliationValues,
                    gradesOffered: gradesValue,
                    country: countryValue || school.country,
                    sportsAvailable: sportsAvailableValue,
                    transportFacilities: transportFacilitiesValue,
                    hostelFacility: hostelFacilityValue,
                })
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Failed to fetch school details",
                life: 3000,
            })
        } finally {
            setPageLoading(false)
        }
    }

    const handleSchoolDataChange = (name, value) => {
        setSchoolData({ ...schoolData, [name]: value })
        setErrors({ ...errors, [name]: "" })
    }

    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Schools", link: "/schoolList" },
        { label: "Edit School", link: "#" },
    ]

    const handleBreadcrumbClick = link => {
        navigate(link)
    }

    const updateSchool = async () => {
        try {
            setLoading(true)

            const payload = {
                ...schoolData,
                country: schoolData?.country?.name,
                type: schoolData?.type?.map(t => t.code || t),
                affiliationBoard: schoolData.affiliationBoard.map(board =>
                    board.code || board
                ),
                sportsAvailable: schoolData.sportsAvailable?.code === "Yes",
                transportFacilities: schoolData.transportFacilities?.code === "Yes",
                hostelFacility: schoolData.hostelFacility?.code === "Yes",
            }

            const config = {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            const response = await SchoolApi.updateSchool(payload, config)

            if (response.data.status) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: response.data.message,
                    life: 3000,
                })
                setTimeout(() => {
                    navigate("/schoolList")
                }, 1000)
            }
        } catch (error) {
            toast.current.show({
                severity: "error",
                summary: "Error",
                detail: error.response?.data?.message || "Failed to update school",
                life: 3000,
            })
        } finally {
            setLoading(false)
        }
    }

    if (pageLoading) {
        return (
            <div className="text-center p-5">
                <Spinner />
            </div>
        )
    }

    return (
        <div className="edit-school">
            <Toast ref={toast} />
            <Row>
                <Col xs="12">
                    <div className="school-list-header d-flex align-items-center justify-content-between">
                        <CustomBreadCrumb
                            data={breadcrumbItems}
                            pageTitle="Edit School"
                            handleBreadcrumbClick={handleBreadcrumbClick}
                        />
                    </div>

                    <Card className="mt-4">
                        <CardBody>
                            <div className="school-add-form">
                                {/* School Name */}
                                <div className="school-add-inp">
                                    <label htmlFor="schoolName">
                                        School Name <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="schoolName"
                                            value={schoolData.schoolName || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("schoolName", e.target.value)
                                            }
                                            placeholder="Enter School Name"
                                        />
                                        {errors.schoolName && (
                                            <small className="error">{errors.schoolName}</small>
                                        )}
                                    </div>
                                </div>

                                {/* Display Name */}
                                <div className="school-add-inp">
                                    <label htmlFor="displayName">
                                        Display Name <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="displayName"
                                            value={schoolData.displayName || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("displayName", e.target.value)
                                            }
                                            placeholder="Enter Display Name"
                                        />
                                        {errors.displayName && (
                                            <small className="error">{errors.displayName}</small>
                                        )}
                                    </div>
                                </div>

                                {/* Type */}
                                <div className="school-add-inp">
                                    <label htmlFor="type">
                                        Type <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <MultiSelect
                                            value={schoolData.type}
                                            onChange={e => handleSchoolDataChange("type", e.value)}
                                            options={typeOptions}
                                            optionLabel="name"
                                            placeholder="Select Type"
                                            display="chip"
                                        />
                                    </div>
                                </div>

                                {/* Medium of Instruction */}
                                <div className="school-add-inp">
                                    <label htmlFor="mediumOfInstruction">Medium of Instruction</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="mediumOfInstruction"
                                            value={schoolData.mediumOfInstruction || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("mediumOfInstruction", e.target.value)
                                            }
                                            placeholder="Enter Medium of Instruction"
                                        />
                                    </div>
                                </div>

                                {/* Year Established */}
                                <div className="school-add-inp">
                                    <label htmlFor="yearEstablished">Year Established</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="yearEstablished"
                                            value={schoolData.yearEstablished || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("yearEstablished", e.target.value)
                                            }
                                            placeholder="Enter Year"
                                        />
                                    </div>
                                </div>

                                {/* Country */}
                                <div className="school-add-inp">
                                    <label htmlFor="country">
                                        Country <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <Dropdown
                                            value={schoolData.country}
                                            onChange={e => handleSchoolDataChange("country", e.value)}
                                            options={Country}
                                            optionLabel="name"
                                            placeholder="Select Country"
                                            filter
                                        />
                                    </div>
                                </div>

                                {/* State */}
                                <div className="school-add-inp">
                                    <label htmlFor="state">
                                        State <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="state"
                                            value={schoolData.state || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("state", e.target.value)
                                            }
                                            placeholder="Enter State"
                                        />
                                    </div>
                                </div>

                                {/* City */}
                                <div className="school-add-inp">
                                    <label htmlFor="city">
                                        City <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="city"
                                            value={schoolData.city || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("city", e.target.value)
                                            }
                                            placeholder="Enter City"
                                        />
                                    </div>
                                </div>

                                {/* Pin Code */}
                                <div className="school-add-inp">
                                    <label htmlFor="pinCode">
                                        Pin Code <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputText
                                            id="pinCode"
                                            value={schoolData.pinCode || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("pinCode", e.target.value)
                                            }
                                            placeholder="Enter Pin Code"
                                        />
                                    </div>
                                </div>

                                {/* Address */}
                                <div className="school-add-inp">
                                    <label htmlFor="address">
                                        Address <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputTextarea
                                            id="address"
                                            value={schoolData.address || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("address", e.target.value)
                                            }
                                            placeholder="Enter Address"
                                            rows={3}
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="school-add-inp">
                                    <label htmlFor="email">Email</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="email"
                                            type="email"
                                            value={schoolData.email || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("email", e.target.value)
                                            }
                                            placeholder="Enter Email"
                                        />
                                    </div>
                                </div>

                                {/* Contact Number */}
                                <div className="school-add-inp">
                                    <label htmlFor="contactNumber">Contact Number</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="contactNumber"
                                            value={schoolData.contactNumber || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("contactNumber", e.target.value)
                                            }
                                            placeholder="Enter Contact Number"
                                        />
                                    </div>
                                </div>

                                {/* Official Website */}
                                <div className="school-add-inp">
                                    <label htmlFor="officialWebsite">Official Website</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="officialWebsite"
                                            value={schoolData.officialWebsite || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("officialWebsite", e.target.value)
                                            }
                                            placeholder="Enter Website URL"
                                        />
                                    </div>
                                </div>

                                {/* Affiliation Board */}
                                <div className="school-add-inp">
                                    <label htmlFor="affiliationBoard">
                                        Affiliation Board <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <MultiSelect
                                            value={schoolData.affiliationBoard}
                                            onChange={e =>
                                                handleSchoolDataChange("affiliationBoard", e.value)
                                            }
                                            options={affiliationBoardOptions}
                                            optionLabel="name"
                                            placeholder="Select Affiliation Boards"
                                            display="chip"
                                        />
                                    </div>
                                </div>

                                {/* About Content */}
                                <div className="school-add-inp">
                                    <label htmlFor="aboutContent">
                                        About Content <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputTextarea
                                            id="aboutContent"
                                            value={schoolData.aboutContent || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("aboutContent", e.target.value)
                                            }
                                            placeholder="Enter About Content"
                                            rows={5}
                                        />
                                    </div>
                                </div>

                                {/* Highlights */}
                                <div className="school-add-inp">
                                    <label htmlFor="highlights">
                                        Highlights <small className="text-danger">*</small>
                                    </label>
                                    <div className="inp-err">
                                        <InputTextarea
                                            id="highlights"
                                            value={schoolData.highlights || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("highlights", e.target.value)
                                            }
                                            placeholder="Enter Highlights"
                                            rows={4}
                                        />
                                    </div>
                                </div>

                                {/* Campus Size */}
                                <div className="school-add-inp">
                                    <label htmlFor="campusSize">Campus Size</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="campusSize"
                                            value={schoolData.campusSize || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("campusSize", e.target.value)
                                            }
                                            placeholder="Enter Campus Size"
                                        />
                                    </div>
                                </div>

                                {/* No of Students */}
                                <div className="school-add-inp">
                                    <label htmlFor="noOfStudents">Number of Students</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="noOfStudents"
                                            value={schoolData.noOfStudents || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("noOfStudents", e.target.value)
                                            }
                                            placeholder="Enter Number of Students"
                                        />
                                    </div>
                                </div>

                                {/* No of Teachers */}
                                <div className="school-add-inp">
                                    <label htmlFor="noOfTeachers">Number of Teachers</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="noOfTeachers"
                                            value={schoolData.noOfTeachers || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("noOfTeachers", e.target.value)
                                            }
                                            placeholder="Enter Number of Teachers"
                                        />
                                    </div>
                                </div>

                                {/* Grades Offered */}
                                <div className="school-add-inp">
                                    <label htmlFor="gradesOffered" className="label">Grades Offered</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="gradesOffered"
                                            value={schoolData.gradesOffered || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("gradesOffered", e.target.value)
                                            }
                                            placeholder="Enter Grades Offered (e.g., Pre-KG to 12th)"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                {/* No of Labs */}
                                <div className="school-add-inp">
                                    <label htmlFor="noOfLabs">Number of Labs</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="noOfLabs"
                                            value={schoolData.noOfLabs || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("noOfLabs", e.target.value)
                                            }
                                            placeholder="Enter Number of Labs"
                                        />
                                    </div>
                                </div>

                                {/* Sports Available */}
                                <div className="school-add-inp">
                                    <label htmlFor="sportsAvailable" className="label">Sports Available</label>
                                    <div className="inp-err">
                                        <Dropdown
                                            id="sportsAvailable"
                                            value={schoolData.sportsAvailable}
                                            onChange={e => handleSchoolDataChange("sportsAvailable", e.value)}
                                            options={yesNoOptions}
                                            optionLabel="name"
                                            placeholder="Select Sports Available"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                {/* Sports Facilities */}
                                {schoolData.sportsAvailable?.code === "Yes" && (
                                    <div className="school-add-inp">
                                        <label htmlFor="sportsFacilities">Sports Facilities</label>
                                        <div className="inp-err">
                                            <InputText
                                                id="sportsFacilities"
                                                value={schoolData.sportsFacilities || ""}
                                                onChange={e =>
                                                    handleSchoolDataChange("sportsFacilities", e.target.value)
                                                }
                                                placeholder="Enter Sports Facilities (e.g., Cricket, Football, Basketball)"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Transport Facilities */}
                                <div className="school-add-inp">
                                    <label htmlFor="transportFacilities" className="label">Transport Facilities</label>
                                    <div className="inp-err">
                                        <Dropdown
                                            id="transportFacilities"
                                            value={schoolData.transportFacilities}
                                            onChange={e => handleSchoolDataChange("transportFacilities", e.value)}
                                            options={yesNoOptions}
                                            optionLabel="name"
                                            placeholder="Select Transport Facilities"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                {/* Hostel Facility */}
                                <div className="school-add-inp">
                                    <label htmlFor="hostelFacility" className="label">Hostel Facility</label>
                                    <div className="inp-err">
                                        <Dropdown
                                            id="hostelFacility"
                                            value={schoolData.hostelFacility}
                                            onChange={e => handleSchoolDataChange("hostelFacility", e.value)}
                                            options={yesNoOptions}
                                            optionLabel="name"
                                            placeholder="Select Hostel Facility"
                                            className="inp-field-box"
                                        />
                                    </div>
                                </div>

                                {/* Yearly Intake */}
                                <div className="school-add-inp">
                                    <label htmlFor="yearlyIntake">Yearly Intake</label>
                                    <div className="inp-err">
                                        <InputText
                                            id="yearlyIntake"
                                            value={schoolData.yearlyIntake || ""}
                                            onChange={e =>
                                                handleSchoolDataChange("yearlyIntake", e.target.value)
                                            }
                                            placeholder="Enter Yearly Intake"
                                        />
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="school-add-btn mt-4">
                                    <Button
                                        color="primary"
                                        onClick={updateSchool}
                                        disabled={loading}
                                        className="me-2"
                                    >
                                        {loading ? <Spinner size="sm" /> : "Update School"}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        onClick={() => navigate("/schoolList")}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default EditSchool
